import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

/**
 * Remote image with the repo's placeholder fallback — the web port of Android's `AttractionImage`.
 *
 * Follows the app's image convention: a plain `<img>` with `(load)`/`(error)`, an opacity fade-in
 * once loaded, and `assets/ImageBroken.svg` on failure. Fills its box with `object-fit: cover`
 * (ContentScale.Crop parity) over a neutral placeholder tint, so it can be sized by the parent.
 */
@Component({
    selector: 'app-attraction-image',
    host: { '[class.loading]': '!loaded' },
    template: `
    <img [src]="src" [alt]="alt" (load)="onLoad()" (error)="onError()"
         [style.opacity]="loaded ? 1 : 0" />
  `,
    styles: [`
    :host {
      display: block;
      overflow: hidden;
      background: var(--attr-surface-variant, #F1F3F5);
    }
    /* Blinking-shadow placeholder while the photo loads. Pulses the host tint —
       NOT position/opacity — so it never disturbs how the mosaic (which sets
       position:absolute on these slices) lays its photos out. */
    :host.loading {
      animation: attrImageShimmer 1.4s ease-in-out infinite;
    }
    @keyframes attrImageShimmer {
      0%, 100% { background-color: var(--attr-shimmer-tint, #B4BECB); }
      50%      { background-color: var(--attr-surface-variant, #F1F3F5); }
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      transition: opacity 0.2s ease;
    }
  `],
    standalone: false
})
export class AttractionImageComponent implements OnChanges {
  @Input() url: string | null = null;
  @Input() alt = '';

  loaded = false;
  errored = false;

  ngOnChanges(changes: SimpleChanges): void {
    // Re-validate ONLY when the image URL itself changes (e.g. the gallery swaps images on the same
    // instance). A change to a sibling input like `alt` — which the attractions FAB binds to a
    // `| translate` value — must NOT reset `loaded`: the <img> src is unchanged, so the browser would
    // never re-fire `load`, leaving the image stuck at opacity 0 (blank) after a language switch.
    if (changes['url']) {
      this.loaded = false;
      this.errored = false;
    }
  }

  get hasUrl(): boolean {
    return !!this.url && this.url.trim().length > 0;
  }

  get src(): string {
    return this.hasUrl && !this.errored ? this.url! : 'assets/ImageBroken.svg';
  }

  onLoad(): void {
    this.loaded = true;
  }

  onError(): void {
    // Fall back to the placeholder; mark loaded so the fade completes on the fallback too.
    this.errored = true;
    this.loaded = true;
  }
}
